import { useEffect, useState } from "react";
import {
  getMessagingTemplates,
  saveMessagingTemplate,
  resetMessagingTemplate,
  type TemplateOverride,
  type TemplateDefault,
} from "../lib/api.ts";

const TEMPLATE_LABELS: Record<string, string> = {
  booking_confirmation: "Booking confirmation (email)",
  booking_cancellation: "Booking cancellation (email)",
  booking_reminder: "Booking reminder (email)",
  sms_confirmation: "Booking confirmation (SMS)",
  sms_cancellation: "Booking cancellation (SMS)",
  sms_reminder: "Booking reminder (SMS)",
};

const TOKEN_HINT = (
  <div
    style={{
      background: "var(--paper-well)",
      border: "1px solid var(--rule)",
      borderRadius: 8,
      padding: "10px 12px",
      fontSize: 12,
      lineHeight: 1.5,
      color: "var(--ink-soft)",
    }}
  >
    <div style={{ fontWeight: 500, color: "var(--ink)", marginBottom: 4 }}>
      Available tokens
    </div>
    <code style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
      {"{{customer_name}}"} {"{{business_name}}"} {"{{service_name}}"} {"{{staff_name}}"} {"{{appointment_time}}"} {"{{appointment_url}}"} {"{{cancel_link}}"}
    </code>
  </div>
);

interface TemplateEditorState {
  subject: string;
  html: string;
  text: string;
  saving: boolean;
  resetting: boolean;
  error: string | null;
  success: boolean;
}

const emptyState: TemplateEditorState = {
  subject: "",
  html: "",
  text: "",
  saving: false,
  resetting: false,
  error: null,
  success: false,
};

export function MessagingTemplates({ slug }: { slug: string }) {
  const [available, setAvailable] = useState<Array<{ id: string; kind: "email" | "sms" }>>([]);
  const [overrides, setOverrides] = useState<Record<string, TemplateOverride>>({});
  const [defaults, setDefaults] = useState<Record<string, TemplateDefault>>({});
  const [editor, setEditor] = useState<Record<string, TemplateEditorState>>({});
  const [loaded, setLoaded] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    getMessagingTemplates(slug)
      .then((s) => {
        setAvailable(s.available);
        setOverrides(s.overrides);
        setDefaults(s.defaults ?? {});
        const init: Record<string, TemplateEditorState> = {};
        for (const a of s.available) {
          const o = s.overrides[a.id];
          const d = (s.defaults ?? {})[a.id];
          // Pre-fill from override when it exists, otherwise from the
          // hardcoded default so the user has copy to edit instead of
          // staring at empty boxes.
          init[a.id] = {
            ...emptyState,
            subject: o?.subject ?? d?.subject ?? "",
            html: o?.html ?? d?.html ?? "",
            text: o?.text ?? d?.text ?? "",
          };
        }
        setEditor(init);
        setLoaded(true);
      })
      .catch((e) => setLoadErr((e as Error).message));
  }, [slug]);

  if (loadErr) return <div className="alert-error">{loadErr}</div>;
  if (!loaded) return <div style={{ color: "var(--ink-faint)" }}>Loading templates…</div>;

  const setField = (id: string, patch: Partial<TemplateEditorState>) =>
    setEditor((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const save = async (id: string, kind: "email" | "sms") => {
    const e = editor[id];
    if (!e.text.trim()) {
      setField(id, { error: "Body text is required.", success: false });
      return;
    }
    setField(id, { saving: true, error: null, success: false });
    try {
      const body =
        kind === "email"
          ? { subject: e.subject, html: e.html, text: e.text }
          : { text: e.text };
      await saveMessagingTemplate(slug, id, body);
      setOverrides((o) => ({
        ...o,
        [id]: {
          template_id: id,
          subject: kind === "email" ? e.subject || null : null,
          html: kind === "email" ? e.html || null : null,
          text: e.text,
          updated_at: new Date().toISOString(),
        },
      }));
      setField(id, { saving: false, success: true });
    } catch (err) {
      setField(id, { saving: false, error: (err as Error).message });
    }
  };

  const reset = async (id: string) => {
    setField(id, { resetting: true, error: null, success: false });
    try {
      await resetMessagingTemplate(slug, id);
      setOverrides((o) => {
        const next = { ...o };
        delete next[id];
        return next;
      });
      // Repopulate the editor from the hardcoded default so the user can
      // see what they've reverted to (and keep editing if they change
      // their mind).
      const d = defaults[id];
      setField(id, {
        resetting: false,
        subject: d?.subject ?? "",
        html: d?.html ?? "",
        text: d?.text ?? "",
      });
    } catch (err) {
      setField(id, { resetting: false, error: (err as Error).message });
    }
  };

  return (
    <section>
      <h2 className="zd-section-title">Templates</h2>
      <p style={{ color: "var(--ink-soft)", fontSize: 14, marginTop: 4, marginBottom: 16 }}>
        Override the wording of the four lifecycle messages we send on your behalf.
        Leave a template empty to use Zedule's default.
      </p>
      {TOKEN_HINT}

      <div style={{ display: "flex", flexDirection: "column", gap: 28, marginTop: 24 }}>
        {available.map((t) => {
          const e = editor[t.id];
          if (!e) return null;
          const isOverride = !!overrides[t.id];
          return (
            <details
              key={t.id}
              open={isOverride}
              style={{
                border: "1px solid var(--rule)",
                borderRadius: 12,
                padding: "14px 16px",
                background: "var(--paper-raised)",
              }}
            >
              <summary style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 500, fontSize: 16, color: "var(--ink)" }}>
                  {TEMPLATE_LABELS[t.id] ?? t.id}
                </span>
                {isOverride ? (
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                      fontSize: 10,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "var(--accent-ink)",
                    }}
                  >
                    custom
                  </span>
                ) : (
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                      fontSize: 10,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "var(--ink-faint)",
                    }}
                  >
                    default
                  </span>
                )}
              </summary>

              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                {t.kind === "email" && (
                  <>
                    <div>
                      <label className="label">Subject</label>
                      <input
                        className="input"
                        value={e.subject}
                        onChange={(ev) => setField(t.id, { subject: ev.target.value })}
                        placeholder="Your appointment with {{business_name}} is confirmed"
                      />
                    </div>
                    <div>
                      <label className="label">HTML body</label>
                      <textarea
                        className="input"
                        rows={8}
                        value={e.html}
                        onChange={(ev) => setField(t.id, { html: ev.target.value })}
                        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12.5 }}
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="label">{t.kind === "email" ? "Plain-text body" : "SMS body"} *</label>
                  <textarea
                    className="input"
                    rows={t.kind === "sms" ? 3 : 6}
                    value={e.text}
                    onChange={(ev) => setField(t.id, { text: ev.target.value })}
                    placeholder={
                      t.kind === "sms"
                        ? "Reminder from {{business_name}}: {{appointment_time}} with {{staff_name}}."
                        : ""
                    }
                  />
                </div>

                {e.error && (
                  <div className="alert-error" style={{ fontSize: 13 }}>{e.error}</div>
                )}
                {e.success && (
                  <div style={{ fontSize: 12.5, color: "var(--status-done)" }}>Saved.</div>
                )}

                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="button"
                    className="zd-hero-cta"
                    disabled={e.saving}
                    onClick={() => save(t.id, t.kind)}
                  >
                    {e.saving ? "Saving…" : "Save template"}
                  </button>
                  {isOverride && (
                    <button
                      type="button"
                      onClick={() => reset(t.id)}
                      disabled={e.resetting}
                      style={{
                        background: "transparent",
                        border: "1px solid var(--rule)",
                        borderRadius: 10,
                        padding: "0 14px",
                        height: 44,
                        fontSize: 13,
                        color: "var(--ink-soft)",
                        cursor: "pointer",
                      }}
                    >
                      {e.resetting ? "Reverting…" : "Revert to default"}
                    </button>
                  )}
                </div>
              </div>
            </details>
          );
        })}
      </div>
    </section>
  );
}
